import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ProjectNode } from 'src/app/shared/models/projectNode';
import { ProjectModel } from 'src/app/shared/models/projectModel';
import { IconService } from 'src/app/shared/services/icon.service';
import { UIIconsEnum, BreadcrumbsIconsEnum } from 'src/app/shared/enums/icons.enum';

@Component({
  selector: 'app-tree-card',
  templateUrl: './tree-card.component.html',
  styleUrls: ['./tree-card.component.scss']
})
export class TreeCardComponent {
  @Input() node!: ProjectNode;
  @Input() isSelected: boolean = false;
  @Output() onModelSelected = new EventEmitter<ProjectModel>();
  @Output() onModelDeselected = new EventEmitter<ProjectModel>();
  @Output() onNodeSelected = new EventEmitter<ProjectNode>();
  @Output() onModelSelection = new EventEmitter<ProjectNode>();

  showModelSelection: boolean = false;
  selectedModels: ProjectModel[] = [];

  constructor(private iconService: IconService) {}

  getIcon(name: keyof typeof BreadcrumbsIconsEnum) {
    return this.iconService.getBreadcrumbsIconByName(name);
  }

  openModelSelection(): void {
    this.showModelSelection = true;
    this.onNodeSelected.emit(this.node);
    if (!this.isSelected) {
      this.onModelSelection.emit(this.node);
    }
  }

  closeModelSelection(): void {
    this.showModelSelection = false;
  }

  onModelSelectionChange(event: {model: ProjectModel, selected: boolean}): void {
    if (event.selected) {
      this.onModelSelected.emit(event.model);
    } else {
      this.onModelDeselected.emit(event.model);
    }
  }

  onCardClick(): void {
    if (this.node.nodes && this.node.nodes.length > 0) {
      this.showModelSelection = false;
      this.onNodeSelected.emit(this.node);
    }
  }

  isModelSelected(model: ProjectModel): boolean {
    return this.selectedModels.some(m => m.id === model.id);
  }

  toggleModelSelection(model: ProjectModel, selected: boolean): void {
    if (selected) {
      if (!this.isModelSelected(model)) {
        this.selectedModels.push(model);
        this.onModelSelected.emit(model);
      }
    } else {
      this.selectedModels = this.selectedModels.filter(m => m.id !== model.id);
      this.onModelDeselected.emit(model);
    }
  }

  // Check if all models under a node are selected
  areAllModelsSelected(node: ProjectNode): boolean {
    const allModels = this.getAllModels(node);
    return allModels.length > 0 && allModels.every(model => this.isModelSelected(model));
  }

  // Get all models under a node recursively
  private getAllModels(node: ProjectNode): ProjectModel[] {
    let models: ProjectModel[] = [];
    if (node.models) {
      models = models.concat(node.models);
    }
    if (node.nodes) {
      node.nodes.forEach(childNode => {
        models = models.concat(this.getAllModels(childNode));
      });
    }
    return models;
  }

  // Toggle selection of a node (select/deselect all models under it)
  toggleNodeSelection(node: ProjectNode, checked: boolean) {
    this.toggleNodeModels(node, checked);
  }

  private toggleNodeModels(node: ProjectNode, checked: boolean) {
    if (node.models) {
      node.models.forEach(model => {
        this.toggleModelSelection(model, checked);
      });
    }
    if (node.nodes) {
      node.nodes.forEach(childNode => {
        this.toggleNodeModels(childNode, checked);
      });
    }
  }

  hasModels(node: ProjectNode): boolean {
    return node.models && node.models?.length > 0 ? true : false;
  }

  hasChild(node: ProjectNode): boolean {
    return node?.nodes && node.nodes.length > 0 ? true : false;
  }
}
